// Navigation Component
// Handles mobile menu toggle and dropdown interactions

export class Navigation {
  private navToggle: HTMLButtonElement | null;
  private navMenu: HTMLElement | null;
  private dropdownToggles: NodeListOf<HTMLButtonElement>;
  private isOpen: boolean = false;

  constructor() {
    this.navToggle = document.querySelector('[data-nav-toggle]');
    this.navMenu = document.querySelector('[data-nav-menu]');
    this.dropdownToggles = document.querySelectorAll('[data-dropdown-toggle]');

    this.init();
  }

  private init(): void {
    if (this.navToggle && this.navMenu) {
      this.navToggle.addEventListener('click', () => this.toggleMenu());
      
      // Close menu when clicking outside
      document.addEventListener('click', (e) => this.handleOutsideClick(e));
      
      // Close menu on escape key
      document.addEventListener('keydown', (e) => this.handleEscapeKey(e));
      
      // Handle window resize
      window.addEventListener('resize', () => this.handleResize());
    }

    // Initialize dropdown toggles
    this.dropdownToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => this.toggleDropdown(e, toggle));
    });
  }

  private toggleMenu(): void {
    this.isOpen = !this.isOpen;
    
    if (this.navToggle && this.navMenu) {
      // Initialize data-nav-open on first toggle to enable transitions
      if (!this.navMenu.hasAttribute('data-nav-open')) {
        this.navMenu.setAttribute('data-nav-open', 'false');
      }
      
      this.navToggle.setAttribute('aria-expanded', String(this.isOpen));
      this.navMenu.setAttribute('data-nav-open', String(this.isOpen));
      
      // Prevent body scroll when menu is open
      if (this.isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  }

  private closeMenu(): void {
    if (this.isOpen) {
      this.isOpen = false;
      
      if (this.navToggle && this.navMenu) {
        this.navToggle.setAttribute('aria-expanded', 'false');
        this.navMenu.setAttribute('data-nav-open', 'false');
        document.body.style.overflow = '';
      }
    }
  }

  private toggleDropdown(event: Event, toggle: HTMLButtonElement): void {
    event.stopPropagation();
    
    // Initialize aria-expanded on first interaction to enable transitions
    if (toggle.getAttribute('aria-expanded') === null) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    
    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
    
    // Close all other dropdowns
    this.dropdownToggles.forEach(otherToggle => {
      if (otherToggle !== toggle) {
        otherToggle.setAttribute('aria-expanded', 'false');
      }
    });
    
    // Toggle current dropdown
    toggle.setAttribute('aria-expanded', String(!isExpanded));
  }

  private handleOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Close menu if clicking outside
    if (this.isOpen && this.navMenu && !this.navMenu.contains(target) && !this.navToggle?.contains(target)) {
      this.closeMenu();
    }
    
    // Close dropdowns if clicking outside
    const isDropdownClick = target.closest('[data-dropdown-toggle]') || target.closest('[data-dropdown-menu]');
    if (!isDropdownClick) {
      this.dropdownToggles.forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }
  }

  private handleEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeMenu();
      
      // Close all dropdowns
      this.dropdownToggles.forEach(toggle => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }
  }

  private handleResize(): void {
    // Close mobile menu when resizing to desktop
    if (window.innerWidth >= 768 && this.isOpen) {
      this.closeMenu();
    }
  }
}
